import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeSwitch from '@/components/ThemeSwitch';
import Button from '@/components/Button';

// Icons
import ChatIcon from '@/assets/chat-box.svg?react';
import PlayIcon from '@/assets/play.svg?react';
import TestIcon from '@/assets/document.svg?react';
import MentorIcon from '@/assets/user-plus.svg?react';
import LegacyIcon from '@/assets/watch.svg?react';
import DoubtIcon from '@/assets/info.svg?react';
import FacebookIcon from '@/assets/facebook.svg?react';
import TwitterIcon from '@/assets/twitter.svg?react';
import InstagramIcon from '@/assets/instagram.svg?react';
import YoutubeIcon from '@/assets/youtube.svg?react';
import LinkedinIcon from '@/assets/linkedin.svg?react';

// Social Icons
const SocialIcon = ({ type }: { type: 'fb' | 'tw' | 'ig' | 'yt' | 'li' }) => {
  const icons = {
    fb: FacebookIcon,
    tw: TwitterIcon,
    ig: InstagramIcon,
    yt: YoutubeIcon,
    li: LinkedinIcon
  };
  const Icon = icons[type];
  return <Icon className="h-6 w-6 text-white transition-colors hover:text-blue-500" />;
};

const DemoEndPage = () => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(15);

  useEffect(() => {
    if (timeLeft <= 0) {
      window.location.href = 'https://aakashdigital.com/';
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, navigate]);

  const features = [
    {
      title: 'Live Interactive Classes',
      desc: "Engage with India's best faculty in real-time. Ask questions and get instant clarifications during the session.",
      icon: <ChatIcon className="h-6 w-6 text-white" />,
      color: 'bg-orange-500'
    },
    {
      title: '24x7 Doubt Resolution',
      desc: 'Never get stuck. Post your doubts anytime on our app and get detailed solutions from subject matter experts.',
      icon: <DoubtIcon className="h-6 w-6 text-white" />,
      color: 'bg-purple-500'
    },
    {
      title: 'Recorded Lectures',
      desc: 'Missed a class? No worries. Access high-quality library of all live sessions for revision and catching up.',
      icon: <PlayIcon className="h-6 w-6 text-white" />,
      color: 'bg-cyan-500'
    },
    {
      title: 'Rigorous Testing',
      desc: 'Weekly tests and All India Test Series (AITS) designed to mimic actual exam patterns and difficulty levels.',
      icon: <TestIcon className="h-6 w-6 text-white" />,
      color: 'bg-green-500'
    },
    {
      title: 'Personalized Mentorship',
      desc: 'Dedicated academic counselors to guide your preparation, track progress, and keep you motivated throughout.',
      icon: <MentorIcon className="h-6 w-6 text-white" />,
      color: 'bg-rose-500'
    },
    {
      title: '36+ Year Legacy',
      desc: 'Benefit from decades of academic excellence and a methodology that has produced thousands of rankers.',
      icon: <LegacyIcon className="h-6 w-6 text-white" />,
      color: 'bg-amber-500'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 transition-colors duration-300 dark:bg-[#0b0f19] dark:text-gray-100">
      {/* Header */}
      <header className="fixed top-0 right-0 left-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md dark:border-gray-800 dark:bg-[#0b0f19]/80">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <img src="/images/ad_logo.webp" alt="Aakash Digital" className="h-8" />
          <div className="flex items-center gap-4">
            <ThemeSwitch
              variant="custom"
              className="rounded-full p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            />
            <Button
              variant="primary"
              onClick={() => (window.location.href = 'https://aakashdigital.com/')}
              className="hidden rounded-full px-6 py-2 font-medium sm:flex"
            >
              Enroll Today
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="animate-in fade-in slide-in-from-bottom-4 mx-auto mb-20 max-w-4xl px-4 text-center duration-1000">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-xs font-bold tracking-wider text-blue-500 uppercase">
            <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
            Your Demo Session Has Ended
          </div>
          <h1 className="mb-6 text-4xl leading-tight font-extrabold md:text-6xl">
            You&apos;ve experienced one class.
            <br />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Now unlock the full journey.
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-gray-600 md:text-xl dark:text-gray-400">
            Structured learning modules. Expert faculty from top institutes. Proven results in JEE,
            NEET & Foundation.
          </p>
          <div className="mb-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              variant="primary"
              onClick={() => (window.location.href = 'https://aakashdigital.com/')}
              className="w-full rounded-xl px-8 py-4 text-lg font-bold shadow-lg shadow-blue-500/20 sm:w-auto"
            >
              Enroll Now
            </Button>
            <Button
              variant="secondary"
              onClick={() => (window.location.href = 'https://aakashdigital.com/')}
              className="w-full rounded-xl px-8 py-4 text-lg font-bold sm:w-auto"
            >
              Explore Courses
            </Button>
          </div>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <div className="h-1 w-48 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
              <div
                className="h-full bg-blue-500 transition-all duration-1000 ease-linear"
                style={{ width: `${(timeLeft / 15) * 100}%` }}
              />
            </div>
            <span>Auto-redirecting in {timeLeft}s...</span>
          </div>
        </section>

        {/* Stats Section */}
        <section className="mx-auto mb-32 max-w-7xl px-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { label: 'AIR 1', sub: 'NEET-UG 2023', color: 'bg-orange-500' },
              { label: 'AIR 1', sub: 'JEE ADVANCED', color: 'bg-blue-600' },
              { label: '98.4%', sub: 'SUCCESS RATE', color: 'bg-green-600' }
            ].map((stat, i) => (
              <div
                key={i}
                className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-6 transition-transform hover:scale-105 dark:border-gray-800 dark:bg-[#151b28]"
              >
                <div
                  className={`${stat.color} flex h-12 w-12 items-center justify-center rounded-xl font-bold text-white`}
                >
                  {stat.label.includes('%') ? '%' : '🏆'}
                </div>
                <div>
                  <div className="text-2xl font-bold">{stat.label}</div>
                  <div className="text-xs font-bold tracking-widest text-gray-500 uppercase">
                    {stat.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features Section */}
        <section className="mx-auto mb-32 max-w-7xl px-4">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-5xl">Why Learn With Us?</h2>
            <div className="mx-auto mb-4 h-1 w-24 rounded-full bg-cyan-500" />
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Premium features designed for top-tier results
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <div
                key={i}
                className="group rounded-3xl border border-gray-100 bg-white p-8 shadow-xl shadow-gray-200/50 transition-all duration-300 hover:border-blue-500/50 dark:border-gray-800 dark:bg-[#151b28] dark:shadow-none"
              >
                <div
                  className={`${f.color} mb-6 flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg shadow-white/20`}
                >
                  {f.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold tracking-tight uppercase transition-colors group-hover:text-blue-500">
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Scholarship Section */}
        <section className="mx-auto max-w-6xl px-4">
          <div className="relative overflow-hidden rounded-[3rem] border border-gray-800 bg-gradient-to-br from-[#0c1221] to-[#1e293b] p-12 text-white shadow-2xl md:p-20">
            <div className="absolute top-0 right-0 -mt-48 -mr-48 h-96 w-96 bg-blue-500/10 blur-[100px]" />
            <div className="relative z-10 flex flex-col items-center justify-between gap-12 md:flex-row">
              <div className="max-w-xl text-center md:text-left">
                <div className="mb-6 inline-block rounded-full bg-rose-500 px-4 py-1 text-xs font-bold tracking-widest uppercase">
                  Limited Period Offer
                </div>
                <h2 className="mb-6 text-4xl font-black md:text-6xl">
                  Get up to <br />
                  <span className="text-600 font-black text-amber-400">60% Scholarship</span>
                </h2>
                <p className="text-lg text-gray-400">
                  Appear for the Admission cum Scholarship Test (ACST) and secure your future with
                  massive fee waivers.
                </p>
              </div>
              <Button
                onClick={() => (window.location.href = 'https://aakashdigital.com/adst')}
                variant="custom"
                className="rounded-2xl bg-white px-10 py-5 text-lg font-black whitespace-nowrap text-black transition-all hover:scale-105 hover:bg-gray-100 active:scale-95"
              >
                Apply for Scholarship
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 pt-20 pb-10 dark:border-gray-800 dark:bg-[#080c14]">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex flex-col items-center text-center">
            <img src="/images/ad_logo.webp" alt="Aakash Digital" className="mb-8 h-10" />
            <h4 className="mb-6 text-xl font-bold">Aakash Educational Services Limited</h4>
            <div className="mb-4 text-xs font-bold tracking-[0.2em] text-blue-500 uppercase">
              Registered Office Address
            </div>
            <p className="mb-8 max-w-lg text-sm leading-loose text-gray-500 italic dark:text-gray-400">
              No. 5/2, 2nd Floor, Kundanahalli Gate, Varthur Road,
              <br />
              Opposite SKR Kalyana Mandapa, Whitefield, Bengaluru 560037
            </p>

            <div className="mb-12 flex flex-col items-center gap-8 text-sm font-medium sm:flex-row">
              <a href="tel:+918800410557" className="flex items-center gap-2 hover:text-blue-500">
                <span>📞</span> +91 8800415537
              </a>
              <a
                href="mailto:corporate@aesl.in"
                className="flex items-center gap-2 hover:text-blue-500"
              >
                <span>✉️</span> corporate@aesl.in
              </a>
            </div>

            <div className="mb-12 flex gap-6">
              <a
                className="hover:bg-primary flex h-10 w-10 transform items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-all hover:-translate-y-1 hover:text-white dark:bg-slate-800 dark:text-slate-300"
                href="https://www.facebook.com/@aakashdigital"
              >
                <SocialIcon type="fb" />
              </a>
              <a
                className="hover:bg-primary flex h-10 w-10 transform items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-all hover:-translate-y-1 hover:text-white dark:bg-slate-800 dark:text-slate-300"
                href="https://x.com/aakash_digital"
              >
                <SocialIcon type="tw" />
              </a>
              <a
                className="hover:bg-primary flex h-10 w-10 transform items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-all hover:-translate-y-1 hover:text-white dark:bg-slate-800 dark:text-slate-300"
                href="https://www.instagram.com/aakashdigital/"
              >
                <SocialIcon type="ig" />
              </a>
              <a
                className="hover:bg-primary flex h-10 w-10 transform items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-all hover:-translate-y-1 hover:text-white dark:bg-slate-800 dark:text-slate-300"
                href="https://www.youtube.com/@Aakash_NEET"
              >
                <SocialIcon type="yt" />
              </a>
              <a
                className="hover:bg-primary flex h-10 w-10 transform items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-all hover:-translate-y-1 hover:text-white dark:bg-slate-800 dark:text-slate-300"
                href="https://www.linkedin.com/school/aakash-digital/"
              >
                <SocialIcon type="li" />
              </a>
            </div>

            <div className="mb-8 h-px w-full bg-gray-200 dark:bg-gray-800" />

            <div className="flex w-full flex-col items-center justify-between gap-4 text-[10px] font-bold tracking-widest text-gray-500 uppercase md:flex-row">
              <div>Copyright © Aakash Educational Services Limited 2025. All Rights Reserved</div>
              <div className="flex gap-8">
                <a href="https://www.aakash.ac.in/termsconditions" className="hover:text-blue-500">
                  Terms and Conditions
                </a>
                <a href="https://www.aakash.ac.in/privacypolicy" className="hover:text-blue-500">
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoEndPage;
